/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Heading, Button } from "@lmig/lmds-react";
import css from 'styled-jsx/css';
import { sortWeightedLottery } from "@lmig/dotcom-aspect-comparion-helpers/sortWeightedLottery";
import AgentListCard from '../Agent/AgentListCard';

const getButtonStyles = (buttonMargin) => {
  if (buttonMargin) {
    return css.resolve`
    .lmig-Button{
      margin-bottom: 2.5rem;
    }
  `;
  }

  return { className: '', styles: '' };
};

const OfficeAgents = ({
  agents: _agents,
  state,
  city,
  buttonText = { show: 'See more agents in this office', hide: 'View less' },
  buttonMargin = false,
  hasCity = false,
  officeMap = []
}) => {
  const agentsPerPage = 6;
  const initialAgentLength = 18;
  const [agents, setAgents] = useState([]);
  const [showAgents, setShowAgents] = useState(false);
  const [agentButtonText, setAgentButtonText] = useState(buttonText.show);
  const [agentsOnLoad, setAgentsOnLoad] = useState(agents.length > agentsPerPage ? agents.slice(0, agentsPerPage) : agents);
  const [nextAgentChunk, setNextAgentChunk] = useState([]);
  const [currentAgentPage, setCurrentAgentPage] = useState(initialAgentLength);
  const { className: buttonClass, styles: buttonStyles } = getButtonStyles(buttonMargin);
  const { className: headingClass, styles: headingStyles } = css.resolve`
    @import './global-styles/_tokens.scss';
    .lm-Heading{
      color: #1a1446;
      font-size: 1.75rem;
      line-height: 1.33;
      margin: 3rem 0 2rem 0;
      display: block;
      text-align: center;

      @media screen and (min-width: $lm-breakpoint-md-min) {
        margin: 4rem 0 2rem 0;
        font-size: 2.5rem;
        line-height: 3rem;
      }
    }
  `;
  const { className: agentClass, styles: agentStyles } = css.resolve`
    *{
      margin-bottom: 2rem;
      width: 100%;
      max-width: 23rem;
    }
  `;

  useEffect(() => {
    const sortedAgents = sortWeightedLottery(_agents);
    setAgentsOnLoad(sortedAgents.length > initialAgentLength ? sortedAgents.slice(0, initialAgentLength) : sortedAgents);
    setAgents(sortedAgents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    if (currentAgentPage >= agents.length) {
      if (showAgents === true) {
        setShowAgents(false);
        setAgentButtonText(buttonText.show);
      } else {
        setAgentButtonText(buttonText.hide);
      }
    } else {
      const newCurrentAgentPage = currentAgentPage + agentsPerPage;
      setCurrentAgentPage(newCurrentAgentPage);
      const nextBatchOfAgents = agents.length > currentAgentPage ? agents.slice(currentAgentPage, currentAgentPage + agentsPerPage) : [];
      setNextAgentChunk([...nextAgentChunk, ...nextBatchOfAgents]);

      if (newCurrentAgentPage >= agents.length) {
        setAgentButtonText(buttonText.hide);
      } else {
        setAgentButtonText(buttonText.show);
      }
    }

    if (showAgents === false) {
      setShowAgents(true);
    }
  };

  return (
    <div className="officeAgentWrapper">
      <span id="agentsInOffice" />
      <Heading type="h2-bold" tag="h2" className={headingClass}>{hasCity ? `Insurance agents in ${city}, ${state}` : `Insurance agents in ${state}`}</Heading>
      <div className="agents">{
        agentsOnLoad.map((agent) => {
          const officeAlias = agent.aliasPath.split('/')[2];
          const officeIndex = officeMap.find(office => office.alias === officeAlias);

          return <AgentListCard key={agent.displayName} className={agentClass} agent={agent} stateName={state} pin={officeIndex && { index: officeIndex.index }} />;
        })
      }</div>
      {!!nextAgentChunk.length && showAgents && <div className="agents agentsRest">{nextAgentChunk.map((agent) => {
        const officeAlias = agent.aliasPath.split('/')[2];
        const officeIndex = officeMap.find(office => office.alias === officeAlias);

        return <AgentListCard key={agent.displayName} className={agentClass} agent={agent} stateName={state} pin={officeIndex && { index: officeIndex.index }} />;
      })}</div>}
      {agents.length > initialAgentLength && <Button className={buttonClass} onClick={onClick} aria-label={agentButtonText}>{agentButtonText}</Button>}
      {headingStyles}
      {agentStyles}
      {buttonStyles}
      <style jsx>{`
        @import './global-styles/_tokens.scss';
        .officeAgentWrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 80rem;
          margin-bottom: 1rem;
          @media screen and (min-width: $lm-breakpoint-sm-min) {
            margin-bottom: 4rem;
          }
        }
        .agents {
          width: 100%;
          display: flex;
          flex-flow: row wrap;
          column-gap: 1rem;
          justify-content: center;
        }
        #agentsInOffice {
          display: block;
          position: relative;
          top: -6rem;
          visibility: hidden;
        }
      `}</style>
    </div >
  );
};

OfficeAgents.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phones: PropTypes.shape({
      primary: PropTypes.shape({
        number: PropTypes.string.isRequired
      }),
      toll: PropTypes.shape({
        number: PropTypes.string.isRequired
      })
    }),
    qualifications: PropTypes.arrayOf(PropTypes.string),
    image: PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    }),
    nNumber: PropTypes.string.isRequired,
    aliasPath: PropTypes.string.isRequired,
  })).isRequired,
  state: PropTypes.string.isRequired,
  city: PropTypes.string,
  buttonText: PropTypes.shape({
    show: PropTypes.string,
    hide: PropTypes.string,
  }),
  buttonMargin: PropTypes.bool,
  hasCity: PropTypes.bool,
  officeMap: PropTypes.array,
};

export default OfficeAgents;
